import cn from 'classnames'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {FormRemoveButton} from './FormRemoveButton'

type FormSelectionProps = {
  title: string
  description?: string
  buttonText?: string
  buttonVariant?: ButtonVariant
  openModal?: () => void
  isAnimated?: boolean
  animationStarted?: boolean
  onRemove?: () => void
}

export const FormSelection: FC<React.PropsWithChildren<FormSelectionProps>> = ({
  title,
  description,
  buttonText,
  buttonVariant,
  openModal,
  isAnimated = false,
  animationStarted = true,
  onRemove,
  children,
}) => (
  <>
    <div
      className={cn('selection', isAnimated && 'animated', animationStarted && 'animation-started')}
    >
      <div className="header">
        <div>
          <Text fontWeight={TextFontWeight.bold}>{title}</Text>
          {description && (
            <div className="subtitle">
              <Text variant={TextVariant.caption} element="p">
                {description}
              </Text>
            </div>
          )}
        </div>
        {onRemove && <FormRemoveButton onRemove={onRemove} />}
      </div>
      {children && <ul className="list">{children}</ul>}
      {buttonText && buttonVariant && openModal && (
        <div className="button-wrapper">
          <Button
            variant={buttonVariant}
            size={ButtonSize.small}
            type="button"
            fullWidth
            onClick={openModal}
          >
            {buttonText}
          </Button>
        </div>
      )}
    </div>

    <style jsx>{`
      .selection {
        padding: 0 ${theme.spacing[4]};
        border-radius: 12px;
        background-color: ${theme.palette.gray[50]};
      }
      .animated {
        opacity: 0;
        visibility: hidden;
        max-height: 0;
        transition: all ${EXPAND_TRANSITION}ms ease-in-out;
      }
      .animation-started {
        opacity: 1;
        visibility: visible;
        max-height: 100%;
        padding: ${theme.spacing[4]};
        margin-top: ${theme.spacing[4]};
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }
      .subtitle {
        margin-top: ${theme.spacing[1]};
      }
      .button-wrapper {
        margin-top: ${theme.spacing[4]};
      }
      .list {
        padding: ${theme.spacing[3]};
        border-radius: 8px;
        background-color: ${theme.palette.background.default};
        box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
        margin-top: ${theme.spacing[4]};
      }
      @media ${MOBILE_SCREENSIZE} {
        .selection {
          padding: 0 ${theme.spacing[3]};
        }
        .animation-started {
          padding: ${theme.spacing[3]};
        }
      }
    `}</style>
  </>
)
